// DS — stany (artboard 07): pusty · błąd · sukces · czeka · 404 — jeden wzorzec:
// glif 48 z zestawu, nagłówek 18, JEDNO zdanie, JEDNA droga (+ kod mono).
// Jedna geometria za pięć (G3-12) i jeden komponent za dwa stany błędu (G3-04).
//
// Ładowanie / pusty / błąd to trzy RÓŻNE komponenty: nieudany odczyt nigdy nie
// wygląda jak „jeszcze nic nie masz”. `Skeleton` daje ładowaniu kształt treści.

import type { CSSProperties, ReactNode } from 'react'
import { Glyph, type GlyphName } from '../media/Glyph'
import Icon from '../media/Icon'
import { Button } from '../primitives/Button'
import { Progress } from '../primitives/Progress'

type Tone = 'accent' | 'danger' | 'warn' | 'muted'

export type StatePanelProps = {
  /** Glif z zestawu ośmiu (utwór, klip, edit, seria, hook, opis, gemy, pomoc). */
  glyph?: GlyphName
  /** @deprecated nazwa lucide — do czasu przepięcia ekranów; glif ma pierwszeństwo. */
  icon?: string
  tone?: Tone
  title: string
  /** Jedno zdanie: co się stało i co można zrobić. */
  body?: ReactNode
  /** Jedna droga. Dwie to maksimum (np. „Pobierz ZIP” + „Otwórz serię”). */
  actions?: ReactNode
  /** Kod błędu / znacznik czasu. Mały, mono, ostatni. */
  detail?: string
  /** Postęp 0..1 (stan „czeka”), bez kręciołka. */
  progress?: number
  className?: string
}

export function StatePanel({ glyph, icon, tone = 'muted', title, body, actions, detail, progress, className = '' }: StatePanelProps) {
  return (
    <div className={['ds-state', className].filter(Boolean).join(' ')}>
      <div className={`ds-state-ic tone-${tone}`} aria-hidden="true">
        {glyph ? <Glyph name={glyph} size={48} tone="current" /> : icon ? <Icon name={icon} size={40} strokeWidth={1.5} /> : null}
      </div>
      <h3>{title}</h3>
      {body && <p>{body}</p>}
      {typeof progress === 'number' && <Progress value={progress} />}
      {actions && <div className="ds-state-actions">{actions}</div>}
      {detail && <div className="ds-state-detail">{detail}</div>}
    </div>
  )
}

/** Jeszcze nic tu nie ma — i to w porządku. Pusty stan SPRZEDAJE następny krok: zawsze niesie drogę. */
export function EmptyState(props: Omit<StatePanelProps, 'tone'>) {
  return <StatePanel {...props} tone="muted" />
}

/**
 * `retryLabel` bez domyślnej wartości, para wymuszona typem: DS nie może wysłać
 * polskiego zdania do każdego języka.
 */
type RetryOffer =
  | { onRetry: () => void; retryLabel: string }
  | { onRetry?: undefined; retryLabel?: undefined }

export function ErrorState({
  title, body, onRetry, retryLabel, detail, glyph = 'edit', icon,
}: {
  title: string
  body?: ReactNode
  detail?: string
  glyph?: GlyphName
  icon?: string
} & RetryOffer) {
  return (
    <StatePanel
      glyph={icon ? undefined : glyph}
      icon={icon}
      tone="muted"
      title={title}
      body={body}
      detail={detail}
      actions={onRetry ? <Button variant="secondary" onClick={onRetry}>{retryLabel}</Button> : undefined}
    />
  )
}

/** Jeden szkielet (G3-11): złóż go w KSZTAŁT treści, która nadchodzi. */
export function Skeleton({
  variant = 'text', width, height, className = '', style,
}: {
  variant?: 'text' | 'title' | 'block' | 'thumb' | 'card' | 'circle'
  width?: number | string
  height?: number | string
  className?: string
  style?: CSSProperties
}) {
  return <div className={`sk sk-${variant} ${className}`.trim()} style={{ width, height, ...style }} aria-hidden="true" />
}

/** N linii — domyślny kształt „lista się ładuje”. */
export function SkeletonLines({ rows = 6, className = '' }: { rows?: number; className?: string }) {
  return (
    <div className={`sk-group ${className}`.trim()} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} variant="text" width={i === rows - 1 ? '60%' : '100%'} />
      ))}
    </div>
  )
}

/** N wierszy listy (miniatura 64×40 + dwie linie + liczba) — artboard 07. */
export function SkeletonRows({ rows = 4, className = '' }: { rows?: number; className?: string }) {
  return (
    <div className={className} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="sk-row">
          <Skeleton variant="thumb" />
          <div className="sk-group" style={{ flex: 1, gap: 8 }}>
            <Skeleton variant="text" width="45%" />
            <Skeleton variant="text" width="30%" height={10} />
          </div>
          <Skeleton variant="text" width={64} />
        </div>
      ))}
    </div>
  )
}

/** N kart (miniatura + tytuł + meta) — artboard 07. */
export function SkeletonCards({ count = 3, ratio = 'card', className = '' }: { count?: number; ratio?: 'card' | 'thumb'; className?: string }) {
  return (
    <div className={`sk-cards ${className}`.trim()} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="sk-group">
          <Skeleton variant={ratio} />
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="40%" height={10} />
        </div>
      ))}
    </div>
  )
}
